import { ReservationStatus, SlotType } from "../models/reservation/reservation.model";
import { CommonThemeColors } from "../components/theme";
import chroma from "chroma-js";
export var getEventColorBasedOnStatus = function (status) {
    switch (status) {
        case ReservationStatus.PENDING:
            return CommonThemeColors.sys.warning.main;
        case ReservationStatus.CONFIRMED:
            return CommonThemeColors.sys.success.main;
        case ReservationStatus.ACTIVE:
            return CommonThemeColors.sys.info.main;
        case ReservationStatus.COMPLETED:
            return CommonThemeColors.sys.palette.blueGray["600"];
        case ReservationStatus.NO_SHOW:
            return '#795548'; // brown — distinct from cancelled
        case ReservationStatus.CANCELLED:
            return CommonThemeColors.sys.error.main;
        default:
            return undefined;
    }
};
export var getEventBackgroundColor = function (status) {
    switch (status) {
        case ReservationStatus.PENDING:
            return CommonThemeColors.sys.warning.background;
        case ReservationStatus.CONFIRMED:
            return CommonThemeColors.sys.success.background;
        case ReservationStatus.ACTIVE:
            return CommonThemeColors.sys.info.background;
        case ReservationStatus.COMPLETED:
            return CommonThemeColors.sys.palette.blueGray["50"];
        case ReservationStatus.NO_SHOW:
            return '#efebe9'; // light brown — distinct from cancelled
        case ReservationStatus.CANCELLED:
            return CommonThemeColors.sys.error.background;
        default:
            return undefined;
    }
};
export var getSlotTypeColor = function (slotType) {
    switch (slotType) {
        case SlotType.RESERVATION:
        case SlotType.SERIAL:
            return CommonThemeColors.sys.success.main;
        case SlotType.BLOCK:
            return CommonThemeColors.sys.error.main;
        default:
            return undefined;
    }
};
export var getSlotTypeBackgroundColor = function (slotType) {
    switch (slotType) {
        case SlotType.RESERVATION:
        case SlotType.SERIAL:
            return CommonThemeColors.sys.success.background;
        case SlotType.BLOCK:
            return CommonThemeColors.sys.error.background;
        default:
            return undefined;
    }
};
export var generateColorVariants = function (color) {
    var base = chroma(color);
    var contrastText = chroma.contrast(base, "#ffffff") >= 3.5 ? "#ffffff" : "#000000";
    return {
        main: base.hex(),
        light: base.brighten(0.8).hex(),
        dark: base.darken(1.2).hex(),
        contrastText: contrastText,
        hover: base.alpha(0.08).hex('rgba'),
        selected: base.alpha(0.16).hex('rgba'),
        focus: base.alpha(0.24).hex('rgba'),
        focusVisible: base.alpha(0.36).hex('rgba'),
        outlinedBorder: base.alpha(0.5).hex('rgba'),
    };
};
export var hexToRgba = function (hex, alpha) {
    // Remove leading #
    hex = hex.replace('#', '');
    // If 8-digit hex, remove alpha channel
    if (hex.length === 8) {
        hex = hex.substring(0, 6);
    }
    var r = parseInt(hex.substring(0, 2), 16);
    var g = parseInt(hex.substring(2, 4), 16);
    var b = parseInt(hex.substring(4, 6), 16);
    return "rgba(".concat(r, ", ").concat(g, ", ").concat(b, ", ").concat(alpha, ")");
};
export var colorToHex8 = function (_a) {
    var r = _a.r, g = _a.g, b = _a.b, a = _a.a;
    var toHex = function (n) { return n.toString(16).padStart(2, "0"); };
    var alphaInt = Math.round(a * 255);
    return "#".concat(toHex(r)).concat(toHex(g)).concat(toHex(b)).concat(toHex(alphaInt));
};
export var hex8ToRgba = function (hex) {
    if (!/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(hex))
        return null;
    var r = parseInt(hex.slice(1, 3), 16);
    var g = parseInt(hex.slice(3, 5), 16);
    var b = parseInt(hex.slice(5, 7), 16);
    var a = hex.length === 9 ? parseInt(hex.slice(7, 9), 16) / 255 : 1;
    return { r: r, g: g, b: b, a: Math.round(a * 100) / 100 };
};
export var getContrastingColor = function (backgroundColor) {
    if (!backgroundColor)
        return;
    // Remove # if present
    var hex = backgroundColor.replace('#', '');
    // Convert to RGB
    var r = parseInt(hex.substr(0, 2), 16);
    var g = parseInt(hex.substr(2, 2), 16);
    var b = parseInt(hex.substr(4, 2), 16);
    // Calculate relative luminance using WCAG formula
    var luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    // Return white for dark backgrounds, black for light backgrounds
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
};
/*
    A PENDING stay is an enquiry or a request still awaiting approval — ASA maps both `request` and
    `waitingList` to it — so the guest is not actually arriving, in-house or leaving on the strength
    of one. Badging those stays showed "In-House" for guests who had nothing but pending requests.
*/
var showsStayStatus = function (stay) {
    return !!stay && stay.status !== ReservationStatus.PENDING;
};
export var getGuestStayStatusInfo = function (profile) {
    var _a;
    /*
        Can assign only one status label

        Product decision:
        Checking in & checking out have higher priority than current stay
        This means that the guest will have checking in status even if he has a current stay as well
    */
    var checkingInToday = profile === null || profile === void 0 ? void 0 : profile.checkingInToday;
    if (showsStayStatus(checkingInToday)) {
        return { status: 'CHECKING_IN', stay: checkingInToday };
    }
    var checkingOutToday = profile === null || profile === void 0 ? void 0 : profile.checkingOutToday;
    if (showsStayStatus(checkingOutToday)) {
        return { status: 'CHECKING_OUT', stay: checkingOutToday };
    }
    var currentStay = profile === null || profile === void 0 ? void 0 : profile.currentStay;
    if (showsStayStatus(currentStay)) {
        return { status: 'STAYING', stay: currentStay };
    }
    var upcomingStay = (_a = profile === null || profile === void 0 ? void 0 : profile.upcomingStays) === null || _a === void 0 ? void 0 : _a.find(showsStayStatus);
    if (upcomingStay) {
        return { status: 'UPCOMING', stay: upcomingStay };
    }
    return undefined;
};
export var getStayStatusBasedOnGuestStayInfo = function (profile) { var _a; return (_a = getGuestStayStatusInfo(profile)) === null || _a === void 0 ? void 0 : _a.status; };
export var getStatusColorBasedOnGuestStayInfo = function (profile) {
    switch (getStayStatusBasedOnGuestStayInfo(profile)) {
        case 'CHECKING_IN':
            return [
                CommonThemeColors.sys.success.main,
                CommonThemeColors.sys.success.background,
                'Checking in',
            ];
        case 'CHECKING_OUT':
            return [
                CommonThemeColors.sys.palette.blueGray["600"],
                CommonThemeColors.sys.palette.blueGray["50"],
                'Checking out',
            ];
        case 'STAYING':
            return [
                CommonThemeColors.sys.info.main,
                CommonThemeColors.sys.info.background,
                'In-House',
            ];
        case 'UPCOMING':
            return [
                CommonThemeColors.sys.warning.main,
                CommonThemeColors.sys.warning.background,
                'Upcoming',
            ];
        default:
            return [undefined, undefined, undefined];
    }
};
export var PRESET_COLORS = [
    'transparent',
    '#3B82F6',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#8B5CF6',
    '#EC4899',
    '#6B7280', // Gray
];
